// FLAG_REGISTRY: array of group objects.
// flattenRegistry(registry): returns flat array of flags with groupId/groupLabel stamped.
// FIXTURES: Map<flagId, () => unknown> — lazy getters for mock data.
// flaggedFetch(fixtureKey, overrides): pure fn — returns fixture or undefined. fixtureKey is the mock flag's id.
export const FLAG_REGISTRY = [
  {
    id: 'workforce',
    label: 'Workforce Command Center',
    blurb: 'Operator console for live agent roster, KPI tiles, and dispatch alerts.',
    flags: [
      { id: 'wcc.route',       type: 'feature', label: 'WCC route',             description: 'Show /workforce in the top nav and mount the page.',                                       defaultValue: false },
      // true: /api/workforce/roster and /api/workforce/kpi don't exist yet; default to mock so WCC renders.
      { id: 'wcc.mock.roster', type: 'mock',    label: 'WCC — agents & roster', description: 'Use fixture agent rows (12 agents across 4 squads) instead of /api/workforce/roster.', defaultValue: false },
      { id: 'wcc.mock.kpi',    type: 'mock',    label: 'WCC — KPI tiles',       description: 'Use fixture KPI counts (throughput, latency, error rate, queue depth).',              defaultValue: false },
      { id: 'wcc.mock.alerts', type: 'mock',    label: 'WCC — dispatch alerts', description: 'Use fixture alert feed; suppresses live SSE subscription.',                           defaultValue: false },
      // JOBE-0163.1: department detail surface (W4). Separate domain from wcc.mock.kpi (CC tiles).
      { id: 'wcc.mock.departments', type: 'mock', label: 'WCC — department detail', description: 'Use fixture per-department goals/OKRs/KPIs/scorecard instead of /api/workforce/departments/:slug.', defaultValue: false },
      // JOBE-0162.1: W3 universal directory (humans + agents fused). Separate flag from wcc.mock.roster
      // (which serves the W2 agent-detail quality strip) to avoid sharing fixture semantics across two surfaces.
      { id: 'wcc.mock.organization', type: 'mock', label: 'WCC — organization directory', description: 'Use fixture ~25-entry humans+agents directory for /organization (W3) instead of fetching /api/agents + /api/departments.', defaultValue: false },
      // JOBE-0161.2: Agent Detail quality KPI mock — split from wcc.mock.roster (JOBE-0161.2 followup).
      { id: 'wcc.mock.agentDetail', type: 'mock', label: 'Agent Detail — quality KPIs', description: 'Use fixture single-agent quality KPIs for W2 quality strip instead of /api/agents/:name/quality.', defaultValue: false },
      // JOBE-0161.3: Agent Detail outcomes table mock.
      { id: 'agent-detail-outcomes-mock', type: 'mock', label: 'Agent Detail — outcomes mock', description: 'Use fixture task outcomes for W2 outcomes table (no real endpoint in v1).', defaultValue: false },
      // JOBE-0160.1: W1 KPI strip — reuses the existing `wcc.mock.kpi` flag (line 14)
      // and overrides the FIXTURES.set in mock-wcc.js to point at `wccKpi` instead.
      // JOBE-0160.2: W1 attention list fixture.
      { id: 'wcc.mock.attention',  type: 'mock', label: 'WCC — attention list', description: 'Use W1 attention-list fixture instead of /api/wcc/attention.',              defaultValue: false },
      // JOBE-0160.3: W1 risk heat map fixture.
      { id: 'wcc.mock.heatmap',    type: 'mock', label: 'WCC — risk heat map',  description: 'Use W1 risk heat map fixture instead of /api/wcc/risk-heatmap.',            defaultValue: false },
      // JOBE-0160.4: W1 trend panel fixture.
      { id: 'wcc.mock.trend',      type: 'mock', label: 'WCC — trend panel',    description: 'Use W1 trend-panel fixture instead of /api/wcc/trends.',                    defaultValue: false },
    ],
  },
  {
    id: 'promptops',
    label: 'PromptOps',
    blurb: 'Fleet management for prompt suites, agent variants, and eval gating.',
    flags: [
      { id: 'promptops.route',             type: 'feature', label: 'PromptOps fleet route',  description: 'Mount /promptops and surface in the top nav.',                                   defaultValue: false },
      // true: PromptOps fleet and suites APIs don't exist yet; default to mock so the pages render.
      { id: 'promptops.mock.fleet',        type: 'mock',    label: 'Fleet metrics',          description: 'Fixture cohort metrics (success rate, p50/p95 latency, drift index).',         defaultValue: false },
      { id: 'promptops.mock.suites',       type: 'mock',    label: 'Eval suites',            description: 'Fixture eval suite catalog and last-run results.',                              defaultValue: false },
      { id: 'promptops.mock.agentDetail',  type: 'mock',    label: 'Agent variant detail',   description: 'Fixture prompt-diff + sampled completions for the agent detail drawer.',       defaultValue: false },
    ],
  },
  {
    id: 'memory',
    label: 'Memory Governance',
    blurb: 'Forgetting queue, conflict review, and provenance audit surfaces.',
    flags: [
      { id: 'memory.governance.route',  type: 'feature', label: 'Governance route',   description: 'Show /memory/governance in the Memory subnav.',                           defaultValue: true  },
      // JOBE-0167.1: list-data fixture for the W8 Memories tab (real /api/memory endpoint exists, so default off).
      { id: 'memory.mock.list',         type: 'mock',    label: 'Memories — list',    description: 'Use fixture memory rows (8 examples across 4 lifecycle states).',         defaultValue: false },
      { id: 'memory.mock.forgetting',   type: 'mock',    label: 'Forgetting queue',   description: 'Fixture queue of pending deletion requests with reason codes.',           defaultValue: false },
      { id: 'memory.mock.conflicts',    type: 'mock',    label: 'Conflict surfacing', description: 'Fixture conflicting-memory pairs awaiting human review.',                 defaultValue: false },
    ],
  },
  {
    id: 'library',
    label: 'Document Library',
    blurb: 'Reader and search experience across ingested documents.',
    flags: [
      { id: 'library.mobile.route', type: 'feature', label: 'Library mobile route', description: 'Mount /m/library, the touch-optimized reader.',                  defaultValue: true  },
      { id: 'library.mock.search',  type: 'mock',    label: 'Search results',       description: 'Fixture hybrid-search hits for the library search bar.',         defaultValue: false },
    ],
  },
];

export function flattenRegistry(registry) {
  const out = [];
  for (const group of registry) {
    for (const flag of group.flags) {
      out.push({ ...flag, groupId: group.id, groupLabel: group.label });
    }
  }
  return out;
}

// FIXTURES: lazy getters keyed by mock flag id. Populated by mock-wcc.js via FIXTURES.set().
export const FIXTURES = new Map();

// Pure function — no store import (avoids circular dependency).
// fixtureKey is the mock flag's id (e.g. 'wcc.mock.roster').
// overrides: get().flags.overrides passed by the caller in store.js.
export function flaggedFetch(fixtureKey, overrides) {
  const flag = flattenRegistry(FLAG_REGISTRY).find(
    (f) => f.type === 'mock' && f.id === fixtureKey
  );
  if (!flag) return undefined;
  const isOn = fixtureKey in overrides ? overrides[fixtureKey] : flag.defaultValue;
  if (!isOn) return undefined;
  const getter = FIXTURES.get(fixtureKey);
  return getter ? getter() : undefined;
}
